import { cn, Spinner, Stack } from 'component-library';
import { ReactNode, useState } from 'react';

import { DefaultWidgetNames } from '@/api/rest/resources/types/widget';
import { SuspenseBoundary } from '@/components';
import { IndicatorCard } from '@/components/IndicatorCard/IndicatorCard';
import { useIndicatorCardConfiguration } from '@/hooks/useIndicatorCardConfiguration';

import { DialogBase, DialogContentWidget } from './Dialog';

export const IndicatorCardDialog = ({
  isOpen,
  toggleIsOpen,
  indicator,
  className,
}: {
  isOpen: boolean;
  toggleIsOpen: () => void;
  indicator: string | DefaultWidgetNames | Array<string | DefaultWidgetNames>;
  className?: string;
}) => {
  const indicators = Array.isArray(indicator) ? indicator : [indicator];
  const shouldShowTabs = indicators.length > 1;
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  return (
    <DialogBase open={isOpen} onOpenChange={toggleIsOpen}>
      <DialogContentWidget className={cn('p-0', className)}>
        {shouldShowTabs ? (
          <div className='p-4'>
            <ControlledTabsList className='mb-4'>
              {indicators.map((ind, index) => (
                <TabTriggerWithConfig
                  key={`${ind}-${index}`}
                  indicator={ind}
                  isActive={activeTabIndex === index}
                  onClick={() => setActiveTabIndex(index)}
                />
              ))}
            </ControlledTabsList>
            {/* eslint-disable-next-line security/detect-object-injection */}
            <IndicatorCardContainer indicator={indicators[activeTabIndex]} withRightMargin={false} />
          </div>
        ) : (
          <IndicatorCardContainer indicator={indicators[0]} />
        )}
      </DialogContentWidget>
    </DialogBase>
  );
};

const IndicatorCardContainer = ({
  indicator,
  withRightMargin = true,
}: {
  indicator?: string | DefaultWidgetNames;
  withRightMargin?: boolean;
}) => {
  const config = useIndicatorCardConfiguration(indicator ?? '');
  if (!config || !config.widget_format) {
    return null;
  }

  if (config.widget_format?.headerProps?.explainerType) {
    config.widget_format = {
      ...config.widget_format,
      styles: {
        ...config.widget_format.styles,
        header: cn(withRightMargin && 'mr-10', config.widget_format.styles?.header),
      },
    };
  }

  return (
    <SuspenseBoundary
      fallback={
        <Stack center className='absolute h-full w-full bg-neutral-white-50'>
          <Spinner size={10} />
        </Stack>
      }
    >
      <IndicatorCard {...config.widget_format} />
    </SuspenseBoundary>
  );
};

const TabTriggerWithConfig = ({
  indicator,
  isActive,
  onClick,
  className,
}: {
  indicator: string | DefaultWidgetNames;
  isActive: boolean;
  onClick: () => void;
  className?: string;
}) => {
  const config = useIndicatorCardConfiguration(indicator);
  const displayName = config?.widget_format?.headerProps?.title ?? indicator;
  return (
    <ControlledTabTrigger isActive={isActive} onClick={onClick} className={className}>
      {displayName}
    </ControlledTabTrigger>
  );
};

// Controlled tabs components for dialog usage
const ControlledTabsList = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <div className={cn('flex overflow-auto', className)}>{children}</div>;
};

const ControlledTabTrigger = ({
  children,
  isActive,
  onClick,
  className,
}: {
  children: ReactNode;
  isActive: boolean;
  onClick: () => void;
  className?: string;
}) => {
  const commonStyles =
    'typography-button1 block border-b-2 p-4 whitespace-nowrap cursor-pointer transition-colors duration-200';

  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        commonStyles,
        isActive
          ? 'border-b-secondary-100 text-primary-100'
          : 'border-transparent text-text-secondary hover:text-text-primary',
        className,
      )}
      tabIndex={0}
      aria-label={`Tab: ${children}`}
    >
      {children}
    </button>
  );
};
